/**
 * Cash Flows tab showing upcoming capital calls and recent distributions.
 * Pulls data from extracted documents and period metrics.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  ArrowDownCircle,
  ArrowUpCircle,
  FileText,
  AlertCircle,
} from "lucide-react";

interface CashFlowsTabProps {
  orgId: string | null;
  familyId?: string | null;
}

interface CashFlowItem {
  id: string;
  fund_name: string;
  amount: number;
  date: string;
  type: "call" | "distribution";
  document_id: string | null;
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Format date
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Check if date is in the future
function isFuture(dateStr: string): boolean {
  return new Date(dateStr) > new Date();
}

// Calculate days until/since date
function daysFromNow(dateStr: string): { days: number; isPast: boolean } {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const days = Math.abs(Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  return { days, isPast: diffMs < 0 };
}

export function CashFlowsTab({ orgId, familyId }: CashFlowsTabProps) {
  // Fetch capital calls from documents
  const { data: capitalCalls, isLoading: callsLoading } = useQuery({
    queryKey: ["capital-calls", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      // Get approved capital call documents
      let query = supabase
        .from("documents")
        .select("id, type, metadata, created_at")
        .eq("type", "capital_call")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;

      // Extract fund name, amount, and due date from metadata
      return (data || []).map((doc) => {
        const extracted = doc.metadata?.extracted_data || {};
        return {
          id: doc.id,
          fund_name: extracted.fund_name || "Unknown Fund",
          amount: extracted.call_amount || 0,
          date: extracted.due_date || extracted.notice_date || doc.created_at,
          type: "call" as const,
          document_id: doc.id,
        };
      });
    },
    enabled: !!orgId,
  });

  // Fetch distributions from documents
  const { data: distributions, isLoading: distLoading } = useQuery({
    queryKey: ["distributions", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from("documents")
        .select("id, type, metadata, created_at")
        .eq("type", "distribution_notice")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;

      return (data || []).map((doc) => {
        const extracted = doc.metadata?.extracted_data || {};
        return {
          id: doc.id,
          fund_name: extracted.fund_name || "Unknown Fund",
          amount: extracted.distribution_amount || extracted.gross_distribution || 0,
          date: extracted.distribution_date || extracted.payment_date || doc.created_at,
          type: "distribution" as const,
          document_id: doc.id,
        };
      });
    },
    enabled: !!orgId,
  });

  // Separate upcoming and past
  const upcomingCalls = capitalCalls?.filter((c) => isFuture(c.date)) || [];
  const recentCalls = capitalCalls?.filter((c) => !isFuture(c.date)).slice(0, 5) || [];
  const recentDistributions = distributions?.slice(0, 10) || [];

  const isLoading = callsLoading || distLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upcoming Capital Calls */}
      <Card className={upcomingCalls.length > 0 ? "border-orange-500/50" : ""}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-orange-600" />
            <CardTitle>Upcoming Capital Calls</CardTitle>
          </div>
          <CardDescription>
            Pending capital contributions due in the next 90 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingCalls.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No upcoming capital calls
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fund</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcomingCalls.map((call) => {
                  const { days } = daysFromNow(call.date);
                  return (
                    <TableRow key={call.id}>
                      <TableCell className="font-medium">{call.fund_name}</TableCell>
                      <TableCell className="text-right font-mono text-orange-600">
                        {formatCurrency(call.amount)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>{formatDate(call.date)}</span>
                          {days <= 7 && (
                            <Badge variant="destructive" className="ml-2">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {days} days
                            </Badge>
                          )}
                          {days > 7 && days <= 30 && (
                            <Badge variant="outline" className="ml-2">
                              {days} days
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {call.document_id && (
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/data/review/${call.document_id}`}>
                              <FileText className="h-4 w-4 mr-1" />
                              View Notice
                            </Link>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Distributions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ArrowDownCircle className="h-5 w-5 text-green-600" />
            <CardTitle>Recent Distributions</CardTitle>
          </div>
          <CardDescription>
            Capital distributions received in the last 90 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentDistributions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No recent distributions
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fund</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentDistributions.map((dist) => (
                  <TableRow key={dist.id}>
                    <TableCell className="font-medium">{dist.fund_name}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      +{formatCurrency(dist.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(dist.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {dist.document_id && (
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/data/review/${dist.document_id}`}>
                            <FileText className="h-4 w-4 mr-1" />
                            View Notice
                          </Link>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recent Capital Calls (Historical) */}
      {recentCalls.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Recent Capital Calls</CardTitle>
            </div>
            <CardDescription>
              Capital calls funded in the last 90 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fund</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentCalls.map((call) => (
                  <TableRow key={call.id}>
                    <TableCell className="font-medium">{call.fund_name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(call.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(call.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {call.document_id && (
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/data/review/${call.document_id}`}>
                            <FileText className="h-4 w-4 mr-1" />
                            View Notice
                          </Link>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
